import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { cssMap, cx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { useResizingHeight } from '@atlaskit/motion';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { usePlatformInstrumentation } from '@atlassian/platform-analytics';

import type { AudioFeatureType } from '../../../api/cc-smarts/models/AudioFeature';
import { TrackSourceType } from '../../../api/cc-smarts/models/TrackSourceType';
import {
	type TrackType,
	TrackType as TrackTypeEnum,
} from '../../../api/cc-smarts/models/TrackType';
import { DEFAULT_SUGGESTION_SIZE } from '../../../constants/playlist';
import { useAudioPlayerStreamingContext } from '../../context/AudioPlayerStreamingProvider';
import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';
import { useAudioPlayerStreamingStore } from '../../hooks/useAudioPlayerStreamingStore';
import { useContextMenuActions } from '../../hooks/useContextMenuActions';
import {
	resetPlaylistDefaultExpanded,
	setActiveContent,
	setPlaylistHasUnavailableContent,
} from '../../state/AudioPlayerState';
import { AudioStreamingState } from '../../state/AudioPlayerStreamingStore';
import { MinifiedAudioPlayer } from '../audio-player/MinifiedAudioPlayer';

import type {
	PlaylistQuery$data,
	PlaylistQuery as PlaylistQueryType,
} from './__generated__/PlaylistQuery.graphql';
import { FooterMessage } from './FooterMessage';
import { PlaylistEmptyView } from './PlaylistEmptyView';
import { PlaylistErrorView } from './PlaylistErrorView';
import { PlaylistHeader } from './PlaylistHeader';
import { type PlaylistItemData } from './PlaylistItem';
import { PlaylistLoadingView } from './PlaylistLoadingView';
import { PlaylistView } from './PlaylistView';
import { SuggestedView } from './SuggestedView';

type UnavailableItem = {
	mainSource: string;
	supportingSources: string[];
	trackType: string;
};

const styles = cssMap({
	wrapper: {
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface'),
		boxShadow: token('elevation.shadow.overflow'),
		position: 'absolute',
		bottom: token('space.100'),
		left: token('space.100'),
		right: token('space.100'),
		display: 'flex',
		flexDirection: 'column',
	},
	contentContainer: {
		height: '414px',
		position: 'relative',
		overflowY: 'auto',
	},
	contentContainerWithPendingContent: {
		height: '476px',
	},
	fullHeightWrapper: {
		flexGrow: 1,
		position: 'relative',
	},
	playlistInnerContainer: {
		flex: 1,
		minHeight: '0px',
		position: 'relative',
		height: '100%',
		paddingTop: token('space.050'),
	},
	coverBorder: {
		position: 'absolute',
		width: '100%',
		top: '0',
		backgroundColor: token('elevation.surface'),
		zIndex: 200,
		height: '12px', //safari Flicker fix hack
		transform: 'translate3d(0, 0, 0)', //safari Flicker fix hack
		// @ts-ignore
		'-webkit-backface-visibility': 'hidden', //safari Flicker fix hack
	},
	stickyShadow: {
		position: 'sticky',
		top: token('space.negative.050'),
		zIndex: 1,
		boxShadow: token('elevation.shadow.overflow'),
		backgroundColor: token('elevation.surface'),
		height: '5px',
	},
	minifiedAudioPlayer: {
		marginLeft: token('space.100'),
		marginRight: token('space.100'),
		marginTop: token('space.100'),
		marginBottom: token('space.100'),
	},
	resizingContainer: {
		minHeight: '0px',
	},
	resizingContainerAnimating: {
		minHeight: '0px',
		overflow: 'hidden',
	},
});

type PlaylistWrapperProps = {
	children: React.ReactNode;
	defaultExpanded?: boolean;
	hasOtherActivePlayback?: boolean;
};

const RESIZE_ANIMATION_FALLBACK_MS = 400;

const PlaylistWrapper = ({
	children,
	defaultExpanded = false,
	hasOtherActivePlayback = false,
}: PlaylistWrapperProps) => {
	const resizingHeightProps = useResizingHeight();
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);
	const [isAnimating, setIsAnimating] = useState(false);
	const resizingRef = useRef<HTMLElement | null>(null);
	const fallbackTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
	const { activeContent, clearPendingContent } = useAudioPlayerStore();
	const { sendUIEvent } = usePlatformInstrumentation();

	const mergedRef = useCallback(
		(element: HTMLElement | null) => {
			resizingRef.current = element;
			resizingHeightProps.ref(element);
		},
		[resizingHeightProps],
	);

	useEffect(() => {
		const element = resizingRef.current;
		if (!element) {
			return () => clearTimeout(fallbackTimeoutRef.current);
		}

		const handleTransitionEnd = (e: TransitionEvent) => {
			if (e.propertyName === 'height') {
				clearTimeout(fallbackTimeoutRef.current);
				setIsAnimating(false);
			}
		};

		element.addEventListener('transitionend', handleTransitionEnd);
		return () => {
			element.removeEventListener('transitionend', handleTransitionEnd);
			clearTimeout(fallbackTimeoutRef.current);
		};
	}, []);

	useEffect(() => {
		setIsExpanded(defaultExpanded);
	}, [defaultExpanded]);

	const handleToggle = () => {
		setIsAnimating(true);
		clearTimeout(fallbackTimeoutRef.current);
		fallbackTimeoutRef.current = setTimeout(
			() => setIsAnimating(false),
			RESIZE_ANIMATION_FALLBACK_MS,
		);
		setIsExpanded((prev: boolean) => {
			// Reset the unavailable content flag
			setPlaylistHasUnavailableContent(false);
			sendUIEvent({
				eventType: 'ui',
				action: !prev ? 'expanded' : 'collapsed',
				actionSubject: 'playlist',
				actionSubjectId: 'playlist',
				source: 'playlist',
			});
			return !prev;
		});
		resetPlaylistDefaultExpanded();
	};

	const handleMinifiedPlayerClick = () => {
		clearPendingContent();
	};

	const shouldShowMinifiedPlayer = !isExpanded && hasOtherActivePlayback;

	return (
		<Flex direction="column" xcss={styles.wrapper}>
			<PlaylistHeader isExpanded={isExpanded} onToggle={handleToggle} />
			{shouldShowMinifiedPlayer && (
				<Box xcss={styles.minifiedAudioPlayer}>
					<MinifiedAudioPlayer
						title={activeContent.title || ''}
						byline=""
						elevated
						onClick={handleMinifiedPlayerClick}
					/>
				</Box>
			)}
			<Box
				ref={mergedRef}
				xcss={isAnimating ? styles.resizingContainerAnimating : styles.resizingContainer}
			>
				{isExpanded && (
					<Flex
						xcss={cx(
							styles.contentContainer,
							hasOtherActivePlayback && styles.contentContainerWithPendingContent,
						)}
						direction="column"
					>
						<Flex direction="column" xcss={styles.fullHeightWrapper}>
							{/* Scroll shadow effect */}
							<Box xcss={styles.coverBorder} />
							<Box xcss={styles.stickyShadow} />
							{children}
						</Flex>
					</Flex>
				)}
			</Box>
		</Flex>
	);
};

type PlaylistInnerProps = {
	cloudId: string;
	fetchKey: number;
	onRetry?: () => void;
};

const PlaylistInner = ({ cloudId, fetchKey, onRetry: onRetrySuggested }: PlaylistInnerProps) => {
	const { fetchAudio } = useAudioPlayerStreamingContext();
	const { activeContent, playlistHasUnavailableContent } = useAudioPlayerStore();
	const { removeTrack, addTrack } = useContextMenuActions(cloudId);
	const { sendTrackEvent } = usePlatformInstrumentation();

	const data = useLazyLoadQuery<PlaylistQueryType>(
		graphql`
			query PlaylistQuery($cloudId: ID!, $includeSuggestions: Boolean!, $popularFeedFirst: Int!) {
				confluence_getPlaylist(cloudId: $cloudId) {
					tracks {
						nodes {
							id
							type
							mainSource
							mainContent {
								id
								title
								type
								status
								subType
								space {
									key
								}
								history {
									createdBy {
										displayName
										profilePicture {
											path
										}
									}
									ownedBy {
										displayName
										profilePicture {
											path
										}
									}
								}
								links {
									base
									webui
								}
								properties(keys: ["emoji-title-published"]) {
									nodes {
										value
									}
								}
							}
							supportingSources
							supportingContent {
								ari
								links {
									base
									webui
								}
							}
							trackSource
						}
					}
				}

				popularFeed(first: $popularFeedFirst) @include(if: $includeSuggestions) {
					edges {
						node {
							content {
								id
								type
								subType
								status
								title
								space {
									name
									id
								}
								links {
									webui
								}
								emoji: properties(key: "emoji-title-published") {
									nodes {
										id
										value
									}
								}
								history {
									createdBy {
										displayName
										profilePicture {
											path(type: RELATIVE_NO_CONTEXT)
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			cloudId,
			includeSuggestions: FeatureGates.getExperimentValue(
				'confluence_audio_updated_playlist_empty_state',
				'isEnabled',
				false,
			),
			popularFeedFirst: 10,
		},
		{
			fetchPolicy: 'network-only',
			fetchKey,
		},
	);

	const { playlistItems: serverPlaylistItems, unavailableItems } = useMemo(() => {
		const tracks = (data as any).confluence_getPlaylist?.tracks?.nodes || [];
		const filtered: PlaylistItemData[] = [];
		const unavailable: UnavailableItem[] = [];

		tracks.forEach((track: any) => {
			const mainContent = track.mainContent;
			const supportingContent = track.supportingContent;
			const hasMainContent = !!mainContent;
			const hasValidStatus = mainContent?.status === 'current' || mainContent?.status === 'draft';

			if (hasMainContent && hasValidStatus) {
				const creator = mainContent.history?.createdBy || mainContent.history?.ownedBy;
				const avatarUrl = creator?.profilePicture?.path || undefined;
				const item: PlaylistItemData = {
					id: track.id,
					title: mainContent.title || undefined,
					trackType: track.type,
					author: creator?.displayName || undefined,
					avatarUrl,
					spaceKey: mainContent.space?.key || '',
					contentType: mainContent.type || '',
					isLive: mainContent.subType === 'live',
					emojiId: mainContent.properties?.nodes?.[0]?.value || undefined,
					contentId: mainContent.id,
					mainContent: {
						ari: track.mainSource,
						link: mainContent.links?.base + mainContent.links?.webui || '',
					},
					supportingContent: supportingContent.map((content: any) => ({
						ari: content.ari,
						link: content.links?.base + content.links?.webui || '',
					})),
					trackSource: track.trackSource,
				};
				filtered.push(item);
			} else {
				const unavailableItem: UnavailableItem = {
					mainSource: track.mainSource,
					supportingSources: track.supportingSources || [],
					trackType: track.type,
				};
				unavailable.push(unavailableItem);
			}
		});

		return { playlistItems: filtered, unavailableItems: unavailable };
	}, [data]);

	const includeSuggestions = FeatureGates.getExperimentValue(
		'confluence_audio_updated_playlist_empty_state',
		'isEnabled',
		false,
	);

	const { suggestedItems } = useMemo(() => {
		const queryData = data as PlaylistQuery$data;
		const popularFeed = queryData.popularFeed;
		const feedEdges = popularFeed?.edges ?? [];
		const filtered: PlaylistItemData[] = [];

		feedEdges.forEach((edge) => {
			if (filtered.length >= DEFAULT_SUGGESTION_SIZE) {
				return;
			}

			const content = edge?.node?.content;
			if (!content) {
				return;
			}

			const hasValidStatus = content.status === 'current' || content.status === 'draft';
			if (!hasValidStatus) {
				return;
			}

			const creator = content.history?.createdBy;
			const avatarUrl = creator?.profilePicture?.path || undefined;
			const contentId = content.id ?? '';
			const item: PlaylistItemData = {
				id: contentId,
				title: content.title || '',
				trackType: TrackTypeEnum.BRIEFING,
				author: creator?.displayName || undefined,
				avatarUrl,
				spaceKey: content.space?.name || content.space?.id || '',
				contentType: content.type || '',
				isLive: content.subType === 'live',
				emojiId: content.emoji?.nodes?.[0]?.value || undefined,
				contentId,
				mainContent: {
					ari: contentId,
					link: content.links?.webui ?? '',
				},
				supportingContent: [],
				trackSource: TrackSourceType.WEB,
			};

			const isInPlaylist = serverPlaylistItems.some(
				(playlistItem) => item.contentId === playlistItem.contentId,
			);

			if (!isInPlaylist) {
				filtered.push(item);
			}
		});

		// When popular feed is missing (e.g. query error), pass undefined so SuggestedView shows error state
		return { suggestedItems: popularFeed?.edges ? filtered : undefined };
	}, [data, serverPlaylistItems]);

	// Remove unavailable items from the playlist and show unavailable content notification
	useEffect(() => {
		if (unavailableItems.length === 0) {
			return;
		}
		setPlaylistHasUnavailableContent(true);
		sendTrackEvent({
			eventType: 'track',
			action: 'detected',
			actionSubject: 'unavailableItems',
			actionSubjectId: 'playlist',
			source: 'playlist',
			attributes: {
				unavailableItemsCount: unavailableItems.length,
				totalPlaylistSize: serverPlaylistItems.length,
			},
		});
		Promise.allSettled(
			unavailableItems.map((item) =>
				removeTrack(item.mainSource, item.supportingSources, item.trackType as TrackType),
			),
		);
	}, [unavailableItems, removeTrack, serverPlaylistItems, sendTrackEvent]);

	const shouldShowFooterMessage = playlistHasUnavailableContent;

	const currentPlaylistItems = serverPlaylistItems;

	const { state: streamingState } = useAudioPlayerStreamingStore();
	const isUninitialized = streamingState.state === AudioStreamingState.UNINITIALIZED;
	const { activeFeature, setActiveFeature } = useAudioPlayerStore();
	const currentItem = useMemo(() => {
		if (!activeContent?.contentId || isUninitialized) {
			return null;
		}
		return activeContent;
	}, [activeContent, isUninitialized]);

	const currentItemTrackType = useMemo(() => {
		if (!activeFeature || isUninitialized) {
			return null;
		}
		// Convert AudioFeatureType (lowercase) to TrackType (uppercase)
		return activeFeature.toString().toUpperCase() as TrackType;
	}, [activeFeature, isUninitialized]);

	const handleItemClick = useCallback(
		(item: PlaylistItemData) => {
			const supportingSources = item.supportingContent.map((content) => content.ari);
			// Add the track to the top of the playlist
			addTrack(item.mainContent.ari, supportingSources, item.trackType, {
				top: true,
				showFlags: false,
				trackSource: item.trackSource,
			}).catch(() => {
				// Silently handle errors - the track will still play even if add fails
				// TODO: analytics
			});
			setActiveContent({
				contentId: item.contentId,
				spaceKey: item.spaceKey || '',
				contentType: item.contentType || '',
				subType: item.isLive ? 'live' : undefined,
				title: item.title,
				author: item.author,
				avatarUrl: item.avatarUrl || '',
				supportingContent: item.supportingContent,
				mainContentLink: item.mainContent.link,
				interimSupportingContent: undefined,
				emojiId: item?.emojiId || undefined,
				trackSource: item.trackSource,
			});
			setActiveFeature(item.trackType.toLowerCase() as AudioFeatureType);
			fetchAudio();
		},
		[addTrack, fetchAudio, setActiveFeature],
	);

	const dismissFooter = useCallback(() => {
		setPlaylistHasUnavailableContent(false);
		sendTrackEvent({
			eventType: 'track',
			action: 'dismissed',
			actionSubject: 'footerMessage',
			actionSubjectId: 'playlist',
			source: 'playlist',
			attributes: {
				unavailableItemsCount: unavailableItems.length,
				totalPlaylistSize: serverPlaylistItems.length,
			},
		});
	}, [sendTrackEvent, unavailableItems.length, serverPlaylistItems.length]);

	return (
		<Flex direction="column" xcss={styles.playlistInnerContainer}>
			{currentPlaylistItems.length === 0 && !currentItem ? (
				<>
					<PlaylistEmptyView suggestedItems={includeSuggestions ? suggestedItems : undefined} />
					{includeSuggestions && (
						<SuggestedView suggestedItems={suggestedItems} onRetry={onRetrySuggested} />
					)}
				</>
			) : (
				<Box>
					<PlaylistView
						items={currentPlaylistItems}
						hasUnavailableItems={unavailableItems.length > 0}
						currentItem={currentItem}
						currentItemTrackType={currentItemTrackType}
						onItemClick={handleItemClick}
						suggestedItems={includeSuggestions ? suggestedItems : undefined}
					/>
					{includeSuggestions && (
						<SuggestedView suggestedItems={suggestedItems} onRetry={onRetrySuggested} />
					)}
				</Box>
			)}
			{shouldShowFooterMessage && <FooterMessage onClose={dismissFooter} />}
		</Flex>
	);
};

type PlaylistProps = {
	cloudId: string;
	defaultExpanded?: boolean;
};

export const Playlist = ({ cloudId, defaultExpanded }: PlaylistProps): React.JSX.Element => {
	const { pendingContent } = useAudioPlayerStore();
	const { state: streamingState } = useAudioPlayerStreamingStore();
	const isUninitialized = streamingState.state === AudioStreamingState.UNINITIALIZED;

	const [fetchKey, setFetchKey] = useState(0);

	const handleIncrementFetchKey = useCallback(() => {
		setFetchKey((prev) => prev + 1);
	}, []);

	return (
		<PlaylistWrapper
			defaultExpanded={defaultExpanded}
			hasOtherActivePlayback={pendingContent !== null && !isUninitialized}
		>
			<ErrorBoundary
				key={fetchKey}
				fallbackRender={(props) => (
					<PlaylistErrorView {...props} onRetry={handleIncrementFetchKey} />
				)}
			>
				<Suspense fallback={<PlaylistLoadingView />}>
					<PlaylistInner cloudId={cloudId} fetchKey={fetchKey} onRetry={handleIncrementFetchKey} />
				</Suspense>
			</ErrorBoundary>
		</PlaylistWrapper>
	);
};
